import React, {FC, useState, useCallback, useRef} from 'react';
import {nanoid} from 'nanoid'
import {Color, MusicPaper, MusicPaperShape} from '@consts/types';
import workPreset from '@consts/workPreset';
import {decode as decodeSqueezeBase64} from '@utils/squeezeBase64';
import {getQueryStringVal} from '@utils/getQueryStringVal';
import PaperMusicDrawer from './parts/drawer';
import {ImportModal} from "./parts/import-modal";
import {ExportModal} from "./parts/export-modal";

const WHITE_COLOR: Color = { alpha: 1, green: 255, red: 255, blue: 255 };
const MUSIC_PAPER_DATA_VERSION = '1.0.0';

const createEmptyMusicPaper = (): MusicPaper => ({
  id: nanoid(),
  title: '',
  version: MUSIC_PAPER_DATA_VERSION,
  shapeList: [],
  backgroundColor: { ...WHITE_COLOR }
})


const PaperMusic: FC = () => {
  const [isUploadModalOpen, setUploadModalState] = useState<boolean>(false);
  const [isDownloadModalOpen, setDownloadModalState] = useState<boolean>(false);

  let defaultPaperMusic: MusicPaper = createEmptyMusicPaper();
  let isPlayModeDefault = false;

  const workName = getQueryStringVal('work');
  if (workName && workPreset[workName]) {
    defaultPaperMusic = decodeSqueezeBase64(workPreset[workName]);
    isPlayModeDefault = true;
  }

  const [musicPaper, setMusicPaper] = useState<MusicPaper>(defaultPaperMusic);

  const [isPlayMode, setPlayMode] = useState<boolean>(isPlayModeDefault);
  const musicPaperRef = useRef<MusicPaper>(musicPaper);
  musicPaperRef.current = musicPaper;

  const uploadMusicPaper = useCallback((data: MusicPaper) => {
    setMusicPaper(data);
    setUploadModalState(false);
  }, [setUploadModalState, setMusicPaper])

  const updateShapeList = useCallback((updateShapeList: MusicPaperShape[]) => {
    setMusicPaper(state => ({ ...state, shapeList: updateShapeList }));
  }, [setMusicPaper])

  const updateBackgroundColor = useCallback((color: Color) => {
    setMusicPaper(state => ({ ...state, backgroundColor: color }));
  }, [setMusicPaper])

  const updateTitle = useCallback((title: string) => {
    if (title.length > 40) return;
    setMusicPaper(state => ({ ...state, title }));
  }, [setMusicPaper])

  const openUploadModal = useCallback(() => {
    setUploadModalState(true);
  }, [setUploadModalState])

  const closeUploadModal = useCallback(() => {
    setUploadModalState(false);
  }, [setUploadModalState])

  const openDownloadModal = useCallback(() => {
    setDownloadModalState(true);
  }, [setDownloadModalState])

  const closeDownloadModal = useCallback(() => {
    setDownloadModalState(false);
  }, [setDownloadModalState])

  return (
    <>
      <PaperMusicDrawer
        title={musicPaper.title}
        shapeList={musicPaper.shapeList}
        backgroundColor={musicPaper.backgroundColor}
        isPlayMode={isPlayMode}
        openUploadModal={openUploadModal}
        openDownloadModal={openDownloadModal}
        updateShapeList={updateShapeList}
        updateBackgroundColor={updateBackgroundColor}
        updateTitle={updateTitle}
        setPlayMode={setPlayMode}
      />
      <ImportModal
        isOpen={isUploadModalOpen}
        uploadMusicPaper={uploadMusicPaper}
        closeUploadModal={closeUploadModal}
      />
      <ExportModal
        isOpen={isDownloadModalOpen}
        musicPaper={musicPaper}
        closeDownloadModal={closeDownloadModal}
      />
    </>
  );
}

export default PaperMusic;
